import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { ArrowCounterClockwise, PencilSimple, Plus, Trash, FloppyDisk, X, Printer, DotsSixVertical } from "@phosphor-icons/react";

const STORAGE_KEY = "vs_checklist_checked";

function loadChecked() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
}

const inputCls =
  "rounded-none border-0 border-b border-border bg-transparent px-0 focus-visible:ring-0 focus-visible:border-accent";

export default function OwnerChecklist({ property }) {
  const [items, setItems] = useState([]);
  const [checked, setChecked] = useState(loadChecked);
  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/checklist");
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(checked));
  }, [checked]);

  const toggle = (id) => setChecked((c) => ({ ...c, [id]: !c[id] }));

  const resetChecks = () => {
    setChecked({});
    toast.success("Checklist reset — ready for the next turnover");
  };

  const printChecklist = () => {
    const today = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
    const groups = items.reduce((acc, it) => {
      (acc[it.category] = acc[it.category] || []).push(it);
      return acc;
    }, {});
    const esc = (s) => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const groupsHtml = Object.entries(groups)
      .map(
        ([cat, list]) => `
        <section class="grp">
          <h2>${esc(cat)}</h2>
          <ul>${list.map((it) => `<li><span class="box"></span><span>${esc(it.text)}</span></li>`).join("")}</ul>
        </section>`
      )
      .join("");
    const w = window.open("", "_blank");
    if (!w) {
      toast.error("Please allow pop-ups to print");
      return;
    }
    w.document.write(`
      <html><head><title>Departure Checklist — ${esc(property?.name || "Via Solana")}</title>
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;700&family=Manrope:wght@400;600&display=swap');
        * { margin:0; box-sizing:border-box; }
        body { font-family:'Manrope',sans-serif; color:#2C3325; padding:48px 44px; }
        .head { border-bottom:2px solid #2C3325; padding-bottom:18px; margin-bottom:26px; display:flex; justify-content:space-between; align-items:flex-end; }
        .over { text-transform:uppercase; letter-spacing:.22em; font-size:11px; font-weight:600; color:#CC7351; }
        h1 { font-family:'Playfair Display',serif; font-size:30px; font-weight:500; margin-top:4px; }
        .meta { text-align:right; font-size:12px; color:rgba(44,51,37,.7); line-height:1.7; }
        .meta .line { display:inline-block; min-width:150px; border-bottom:1px solid rgba(44,51,37,.4); }
        .grid { column-count:2; column-gap:36px; }
        .grp { break-inside:avoid; margin-bottom:22px; }
        .grp h2 { text-transform:uppercase; letter-spacing:.18em; font-size:11px; font-weight:600; color:#CC7351; border-bottom:1px solid rgba(44,51,37,.2); padding-bottom:6px; margin-bottom:10px; }
        ul { list-style:none; }
        li { display:flex; align-items:flex-start; gap:10px; font-size:13px; line-height:1.45; padding:5px 0; }
        .box { width:14px; height:14px; border:1.5px solid #2C3325; border-radius:2px; flex-shrink:0; margin-top:2px; }
        .foot { margin-top:30px; padding-top:16px; border-top:1px solid rgba(44,51,37,.2); font-size:12px; color:rgba(44,51,37,.7); display:flex; gap:40px; }
        @media print { body { padding:24px; } }
      </style></head>
      <body onload="setTimeout(function(){window.print()},350)">
        <div class="head">
          <div><div class="over">Departure Checklist</div><h1>${esc(property?.name || "Via Solana")}</h1></div>
          <div class="meta">Date: <span class="line"></span><br/>Completed by: <span class="line"></span></div>
        </div>
        <div class="grid">${groupsHtml}</div>
        <div class="foot"><span>Generated ${esc(today)}</span><span>Notes: ____________________________________________</span></div>
      </body></html>`);
    w.document.close();
  };

  const grouped = useMemo(() => {
    const g = {};
    items.forEach((it) => {
      (g[it.category] = g[it.category] || []).push(it);
    });
    return g;
  }, [items]);

  const total = items.length;
  const done = items.filter((it) => checked[it.id]).length;
  const pct = total ? Math.round((done / total) * 100) : 0;

  // ---- edit mode ----
  const startEdit = () => {
    setDraft(items.map((it) => ({ ...it })));
    setEditMode(true);
  };
  const cancelEdit = () => setEditMode(false);
  const setDraftField = (idx, field, value) =>
    setDraft((d) => d.map((it, i) => (i === idx ? { ...it, [field]: value } : it)));
  const addDraftItem = () =>
    setDraft((d) => [...d, { id: "", category: d[d.length - 1]?.category || "General", text: "", order: d.length }]);
  const removeDraftItem = (idx) => setDraft((d) => d.filter((_, i) => i !== idx));

  // ---- drag & drop reorder (edit mode) ----
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const moveDraftItem = (from, to) => {
    if (from === null || from === to) return;
    setDraft((d) => {
      const next = [...d];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
    setDragIdx(to);
  };

  const saveDraft = async () => {
    const cleaned = draft.filter((it) => (it.text || "").trim());
    setSaving(true);
    try {
      await api.put("/admin/checklist", { items: cleaned });
      toast.success("Checklist saved");
      setEditMode(false);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className="text-muted-foreground" data-testid="checklist-loading">Loading checklist…</p>;
  }

  return (
    <div className="max-w-3xl" data-testid="owner-checklist">
      <div className="flex items-start justify-between gap-4">
        <div>
          <span className="cms-label text-accent">Owner Tools</span>
          <h2 className="font-display text-4xl tracking-tight text-foreground mt-2">Departure Checklist</h2>
          <p className="text-muted-foreground mt-3 text-sm max-w-lg">
            Confirm the home is in order after a stay and ready for the next guest. Check-off
            state is saved on this device — reset it before each new departure.
          </p>
        </div>
        {!editMode ? (
          <div className="shrink-0 flex items-center gap-2">
            <button
              onClick={printChecklist}
              data-testid="print-checklist-btn"
              className="inline-flex items-center gap-2 border border-border px-4 py-2 text-sm hover:bg-secondary transition-colors"
            >
              <Printer size={15} /> Print / PDF
            </button>
            <button
              onClick={startEdit}
              data-testid="edit-checklist-btn"
              className="inline-flex items-center gap-2 bg-foreground text-primary-foreground px-4 py-2 rounded-full text-sm font-semibold hover:bg-accent transition-colors duration-300"
            >
              <PencilSimple size={15} weight="bold" /> Edit items
            </button>
          </div>
        ) : null}
      </div>

      {!editMode ? (
        <>
          {/* Progress */}
          <div className="mt-6 mb-8 border border-border px-5 py-4 bg-secondary/40 flex flex-wrap items-center gap-x-6 gap-y-3" data-testid="checklist-progress">
            <span className="cms-label text-foreground/60">Progress</span>
            <div className="flex-1 min-w-36">
              <Progress value={pct} className="h-2" />
            </div>
            <span className="font-display text-xl text-foreground">
              {done}<span className="text-muted-foreground text-base"> / {total}</span>
            </span>
            <span className="text-sm text-muted-foreground">
              {pct === 100 ? "All set — ready for the next guest." : `${pct}%`}
            </span>
            <button
              onClick={resetChecks}
              data-testid="reset-checklist-btn"
              className="inline-flex items-center gap-2 text-sm text-accent hover:opacity-70 transition-opacity ml-auto"
            >
              <ArrowCounterClockwise size={15} /> Reset for new rental
            </button>
          </div>

          {/* Grouped items — compact two-column */}
          <div className="grid md:grid-cols-2 gap-x-10 gap-y-6">
            {Object.entries(grouped).map(([cat, list]) => (
              <div key={cat} data-testid={`checklist-group-${cat}`}>
                <p className="cms-label text-accent mb-3">{cat}</p>
                <div className="space-y-px bg-border border border-border">
                  {list.map((it) => {
                    const isDone = !!checked[it.id];
                    return (
                      <label
                        key={it.id}
                        data-testid={`checklist-item-${it.id}`}
                        className="flex items-start gap-3 bg-background px-4 py-2.5 cursor-pointer group"
                      >
                        <Checkbox
                          checked={isDone}
                          onCheckedChange={() => toggle(it.id)}
                          data-testid={`checkbox-${it.id}`}
                          className="mt-0.5 data-[state=checked]:bg-accent data-[state=checked]:border-accent"
                        />
                        <span
                          className={
                            "text-sm leading-snug transition-colors " +
                            (isDone ? "line-through text-muted-foreground" : "text-foreground/90")
                          }
                        >
                          {it.text}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        /* ---- Edit mode ---- */
        <div className="mt-8" data-testid="checklist-editor">
          <p className="text-xs text-muted-foreground mb-4 flex items-center gap-2">
            <DotsSixVertical size={14} /> Drag items by their handle to reorder, then save.
          </p>
          <div className="space-y-3">
            {draft.map((it, idx) => (
              <div
                key={idx}
                draggable
                onDragStart={() => setDragIdx(idx)}
                onDragOver={(e) => {
                  e.preventDefault();
                  setOverIdx(idx);
                  moveDraftItem(dragIdx, idx);
                }}
                onDragEnd={() => {
                  setDragIdx(null);
                  setOverIdx(null);
                }}
                className={
                  "flex gap-3 items-center border p-3 bg-secondary/40 transition-colors " +
                  (dragIdx === idx ? "border-accent opacity-70 " : "border-border ") +
                  (overIdx === idx && dragIdx !== idx ? "border-t-accent" : "")
                }
                data-testid={`draft-item-${idx}`}
              >
                <span
                  className="cursor-grab active:cursor-grabbing text-muted-foreground/60 hover:text-foreground transition-colors shrink-0"
                  data-testid={`draft-drag-${idx}`}
                  title="Drag to reorder"
                >
                  <DotsSixVertical size={18} weight="bold" />
                </span>
                <Input
                  value={it.category}
                  onChange={(e) => setDraftField(idx, "category", e.target.value)}
                  placeholder="Category"
                  className={inputCls + " w-44 shrink-0"}
                  data-testid={`draft-category-${idx}`}
                />
                <Input
                  value={it.text}
                  onChange={(e) => setDraftField(idx, "text", e.target.value)}
                  placeholder="Checklist item"
                  className={inputCls + " flex-1"}
                  data-testid={`draft-text-${idx}`}
                />
                <button
                  type="button"
                  onClick={() => removeDraftItem(idx)}
                  data-testid={`draft-remove-${idx}`}
                  className="text-destructive/70 hover:text-destructive transition-colors shrink-0"
                >
                  <Trash size={18} />
                </button>
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={addDraftItem}
            data-testid="add-checklist-item-btn"
            className="mt-4 w-full inline-flex items-center justify-center gap-2 border border-dashed border-border py-3 text-sm text-muted-foreground hover:border-accent hover:text-accent transition-colors"
          >
            <Plus size={16} weight="bold" /> Add item
          </button>

          <div className="flex items-center gap-3 mt-8">
            <button
              onClick={saveDraft}
              disabled={saving}
              data-testid="save-checklist-btn"
              className="inline-flex items-center gap-2 bg-foreground text-primary-foreground pl-6 pr-7 py-3.5 rounded-full text-sm font-semibold hover:bg-accent transition-colors duration-300 disabled:opacity-60"
            >
              <FloppyDisk size={16} weight="bold" /> {saving ? "Saving…" : "Save checklist"}
            </button>
            <button
              onClick={cancelEdit}
              data-testid="cancel-checklist-btn"
              className="inline-flex items-center gap-2 border border-border px-5 py-3.5 rounded-full text-sm hover:bg-secondary transition-colors"
            >
              <X size={16} /> Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
